import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Star, Lock, Play, ChevronLeft, Share2, Download, Copy, Check } from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

/* ─── DATA ─── */
const RANKS = [
  { min: 0, name: 'Sleepwalker', icon: '🌱' },
  { min: 50, name: 'Seeker', icon: '🔍' },
  { min: 150, name: 'Awakener', icon: '✨' },
  { min: 300, name: 'Alchemist', icon: '🔮' },
  { min: 500, name: 'Legend', icon: '🔥' },
];

const QUIZ_QUESTIONS = [
  { q: 'When life throws you a curveball, your first instinct is to...', opts: [
    { text: 'Create something new from the wreckage', type: 'Creator' },
    { text: 'Stand up and fight through it', type: 'Warrior' },
    { text: 'Search for a deeper meaning behind it', type: 'Seeker' },
    { text: 'Help others going through the same thing', type: 'Healer' },
  ]},
  { q: 'The words that move you most are...', opts: [
    { text: '"I create my own reality"', type: 'Creator' },
    { text: '"I will not be defeated"', type: 'Warrior' },
    { text: '"There must be something more"', type: 'Seeker' },
    { text: '"Let me tell you my story"', type: 'Storyteller' },
  ]},
  { q: 'In your friend group, you are known as...', opts: [
    { text: 'The one with big ideas', type: 'Creator' },
    { text: 'The one who never quits', type: 'Warrior' },
    { text: 'The one who wakes people up', type: 'Awakener' },
    { text: 'The one everyone comes to for healing', type: 'Healer' },
  ]},
  { q: 'Your biggest fear is...', opts: [
    { text: 'Living an unlived life', type: 'Awakener' },
    { text: 'Losing my voice', type: 'Storyteller' },
    { text: 'Never finding my purpose', type: 'Seeker' },
    { text: 'Not being strong enough', type: 'Warrior' },
  ]},
  { q: 'When you look in the mirror, you want to see...', opts: [
    { text: 'The person I was meant to become', type: 'Awakener' },
    { text: 'Someone who has created beauty from pain', type: 'Creator' },
    { text: 'A soul at peace with its story', type: 'Storyteller' },
    { text: 'Someone who healed themselves AND others', type: 'Healer' },
  ]},
];

const VISION_ITEMS = [
  { id: 1, label: 'Inner Peace', icon: '🕊️' },
  { id: 2, label: 'Financial Freedom', icon: '💰' },
  { id: 3, label: 'Creative Expression', icon: '🎨' },
  { id: 4, label: 'Deep Love', icon: '❤️' },
  { id: 5, label: 'Physical Health', icon: '💪' },
  { id: 6, label: 'Spiritual Growth', icon: '🌿' },
  { id: 7, label: 'Community', icon: '🤝' },
  { id: 8, label: 'Legacy', icon: '👑' },
  { id: 9, label: 'Adventure', icon: '🌍' },
  { id: 10, label: 'Knowledge', icon: '📖' },
  { id: 11, label: 'Purpose', icon: '🎯' },
  { id: 12, label: 'Joy', icon: '✨' },
];

const ARCHETYPE_INFO = {
  Creator: { tagline: 'I build worlds from wreckage.', color: '#D4AF37', desc: 'You see possibilities where others see dead ends. Your gift is turning nothing into something — and something into everything.' },
  Warrior: { tagline: 'I rise every time I fall.', color: '#E8553A', desc: 'You don\'t quit. You don\'t fold. When the world pushes, you push harder. Your strength isn\'t just physical — it\'s spiritual.' },
  Seeker: { tagline: 'I know there is something more.', color: '#4A90D9', desc: 'You\'ve always felt it — that quiet knowing that there\'s a deeper truth waiting. You won\'t stop until you find it.' },
  Awakener: { tagline: 'I open eyes — starting with my own.', color: '#9B59B6', desc: 'You wake people up just by being in the room. Your presence is a mirror, and your words are an alarm clock for the soul.' },
  Healer: { tagline: 'I mend what others thought was broken.', color: '#50C878', desc: 'You carry the gift of restoration. You see the wound beneath the wound and know exactly how to tend to it.' },
  Storyteller: { tagline: 'My story is my superpower.', color: '#E8A838', desc: 'Your truth is your weapon and your gift. When you speak, people don\'t just listen — they feel. Your story gives others permission to tell theirs.' },
};

const SITE_URL = 'https://thealchemyvillage.com/journey';

/* ─── SHARE CARD ─── */
function ShareCard({ alchemistType, visions, onClose }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const info = ARCHETYPE_INFO[alchemistType] || ARCHETYPE_INFO.Creator;

  const captureCard = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
    });
    return canvas;
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const canvas = await captureCard();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = `alchemist-${alchemistType.toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Card saved! Share it everywhere.');
    } catch { toast.error('Could not generate image.'); }
    setDownloading(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(SITE_URL);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareTwitter = () => {
    const text = `I just completed The Alchemy Journey and discovered I'm The ${alchemistType}. "${info.tagline}" — What's YOUR Alchemist Type?`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(SITE_URL)}`, '_blank');
  };

  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}&quote=${encodeURIComponent(`I'm The ${alchemistType}. What's your Alchemist Type?`)}`, '_blank');
  };

  return (
    <div className="aj-share-overlay" data-testid="aj-share-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="aj-share-modal" data-testid="aj-share-modal">
        <button className="aj-share-close" onClick={onClose} data-testid="aj-share-close">&times;</button>
        <h3 className="aj-share-heading">Share Your Alchemist Type</h3>

        {/* The Card */}
        <div ref={cardRef} className="aj-share-card" data-testid="aj-share-card" style={{ borderColor: info.color }}>
          <div className="aj-share-card-inner">
            <p className="aj-share-card-label">THE ALCHEMY VILLAGE</p>
            <h2 className="aj-share-card-type" style={{ color: info.color }}>The {alchemistType}</h2>
            <p className="aj-share-card-tagline">"{info.tagline}"</p>
            <p className="aj-share-card-desc">{info.desc}</p>
            {visions && visions.length > 0 && (
              <div className="aj-share-card-visions">
                <p className="aj-share-card-vlabel">My Vision</p>
                <div className="aj-share-card-vtags">
                  {visions.map((v, i) => <span key={i} style={{ borderColor: `${info.color}44` }}>{v}</span>)}
                </div>
              </div>
            )}
            <p className="aj-share-card-cta">What's YOUR Alchemist Type?</p>
            <p className="aj-share-card-url">thealchemyvillage.com/journey</p>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="aj-share-actions" data-testid="aj-share-actions">
          <button className="aj-share-btn aj-share-download" onClick={handleDownload} disabled={downloading} data-testid="aj-share-download">
            <Download size={16} /> {downloading ? 'Saving...' : 'Save Image'}
          </button>
          <button className="aj-share-btn aj-share-copy" onClick={handleCopyLink} data-testid="aj-share-copy">
            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button className="aj-share-btn aj-share-twitter" onClick={handleShareTwitter} data-testid="aj-share-twitter">
            𝕏 Share on X
          </button>
          <button className="aj-share-btn aj-share-facebook" onClick={handleShareFacebook} data-testid="aj-share-facebook">
            f Share on Facebook
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── MAZE GENERATOR ─── */
function generateMaze(rows, cols) {
  const grid = Array.from({ length: rows }, () => Array(cols).fill(1));
  const visited = Array.from({ length: rows }, () => Array(cols).fill(false));

  function carve(r, c) {
    visited[r][c] = true;
    grid[r][c] = 0;
    const dirs = [[0,2],[0,-2],[2,0],[-2,0]].sort(() => Math.random() - 0.5);
    for (const [dr, dc] of dirs) {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && !visited[nr][nc]) {
        grid[r + dr/2][c + dc/2] = 0;
        carve(nr, nc);
      }
    }
  }
  carve(0, 0);
  grid[0][0] = 0;
  grid[rows - 1][cols - 1] = 0;
  return grid;
}

/* ─── COMPONENTS ─── */

function XPBar({ xp }) {
  const rank = [...RANKS].reverse().find(r => xp >= r.min) || RANKS[0];
  const nextRank = RANKS.find(r => r.min > xp);
  const pct = nextRank ? ((xp - rank.min) / (nextRank.min - rank.min)) * 100 : 100;

  return (
    <div className="aj-xp" data-testid="aj-xp-bar">
      <span className="aj-xp-badge">{rank.icon}</span>
      <div className="aj-xp-info">
        <span className="aj-xp-rank">{rank.name}</span>
        <div className="aj-xp-track"><div className="aj-xp-fill" style={{ width: `${pct}%` }} /></div>
      </div>
      <span className="aj-xp-num">{xp} XP</span>
    </div>
  );
}

function WelcomeScreen({ onBegin, onMap }) {
  return (
    <div className="aj-screen aj-welcome" data-testid="aj-welcome">
      <div className="aj-welcome-orb">🌿</div>
      <h1 className="aj-main-title">The Alchemy Journey</h1>
      <p className="aj-main-sub">Four Levels. One Transformation.</p>
      <p className="aj-tagline">F.U.C.K. T.H.A.T.T. · Be. Do. Have.</p>
      <div className="aj-guide" data-testid="aj-guide-message">
        <p className="aj-guide-name">Your Guide — Zuri the Hummingbird</p>
        <p className="aj-guide-text">"Welcome, Manifestor. You have arrived here for a reason. The Alchemy Village holds the answers to the three most important questions you will ever ask yourself — and I will guide you through every level. Are you ready to stop sleepwalking and become the Alchemist you were born to be?"</p>
      </div>
      <button className="village-btn village-btn--primary aj-btn" data-testid="aj-begin-btn" onClick={onBegin}>Begin My Journey</button>
      <button className="village-btn village-btn--secondary aj-btn" data-testid="aj-map-btn" onClick={onMap}>View Level Map</button>
    </div>
  );
}

function LevelMap({ unlocked, onSelect, onBack }) {
  const levels = [
    { num: 1, q: 'Who Are You?', name: 'The Awakening', desc: 'Discover your Alchemist Type', unlock: 'Your Alchemist Profile', icon: '🔮' },
    { num: 2, q: 'What Do You Want?', name: 'The Maze', desc: 'Navigate the Maze of Life', unlock: 'Alchemist Blueprint PDF', icon: '🗺️' },
    { num: 3, q: 'Why Do You Want It?', name: 'The Vision', desc: 'Build your Alchemy Vision Board', unlock: 'Video Coaching Session', icon: '🎯' },
    { num: 4, q: 'Be. Do. Have.', name: 'The Harvest', desc: 'Your transformation revealed', unlock: 'LIVE Coaching + God in ME', icon: '🌾' },
  ];

  return (
    <div className="aj-screen aj-map" data-testid="aj-level-map">
      <h2 className="aj-section-title">The Alchemy Journey</h2>
      <p className="aj-section-sub">Choose Your Level</p>
      <div className="aj-levels">
        {levels.map(lv => {
          const isOpen = lv.num <= unlocked;
          return (
            <button
              key={lv.num}
              className={`aj-level-card ${isOpen ? '' : 'aj-locked'}`}
              data-testid={`aj-level-${lv.num}`}
              onClick={() => isOpen && onSelect(lv.num)}
              disabled={!isOpen}
            >
              <span className="aj-level-icon">{lv.icon}</span>
              <div className="aj-level-info">
                <span className="aj-level-num">Level {lv.num} · {lv.q}</span>
                <span className="aj-level-name">{lv.name}</span>
                <span className="aj-level-desc">{lv.desc}</span>
                <span className="aj-level-unlock">Unlocks: {lv.unlock}</span>
              </div>
              <span className="aj-level-status">{isOpen ? <Play size={16} /> : <Lock size={16} />}</span>
            </button>
          );
        })}
      </div>
      <button className="aj-back" data-testid="aj-back-welcome" onClick={onBack}><ChevronLeft size={16} /> Back</button>
    </div>
  );
}

function QuizLevel({ onComplete, onBack }) {
  const [qi, setQi] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [showShare, setShowShare] = useState(false);

  const handleAnswer = (type) => {
    const next = [...answers, type];
    setAnswers(next);
    if (qi + 1 < QUIZ_QUESTIONS.length) {
      setQi(qi + 1);
    } else {
      const counts = {};
      next.forEach(t => { counts[t] = (counts[t] || 0) + 1; });
      const winner = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      setResult(winner);
    }
  };

  if (result) {
    return (
      <div className="aj-screen aj-result" data-testid="aj-quiz-result">
        <div className="aj-result-orb">🔮</div>
        <h2 className="aj-alchemist-type">The {result}</h2>
        <p className="aj-section-sub">Your Alchemist Type has been revealed.</p>
        <div className="aj-guide" style={{ marginTop: '1.5rem' }}>
          <p className="aj-guide-name">Zuri speaks</p>
          <p className="aj-guide-text">"I see it now. You are The {result}. This is the energy you carry into the world — and the gift you will use to transform your life. Remember this. Carry it with you."</p>
        </div>
        <div className="aj-result-btns">
          <button className="village-btn village-btn--secondary aj-btn" data-testid="aj-quiz-share" onClick={() => setShowShare(true)}>
            <Share2 size={16} /> Share My Type
          </button>
          <button className="village-btn village-btn--primary aj-btn" data-testid="aj-quiz-continue" onClick={() => onComplete(result)}>Continue to Level 2</button>
        </div>
        {showShare && <ShareCard alchemistType={result} onClose={() => setShowShare(false)} />}
      </div>
    );
  }

  const question = QUIZ_QUESTIONS[qi];
  return (
    <div className="aj-screen aj-quiz" data-testid="aj-quiz">
      <button className="aj-back" onClick={onBack}><ChevronLeft size={16} /> Level Map</button>
      <h2 className="aj-section-title">Level 1: The Awakening</h2>
      <p className="aj-section-sub">Who Are You? Discover your Alchemist Type</p>
      <div className="aj-dots">
        {QUIZ_QUESTIONS.map((_, i) => (
          <span key={i} className={`aj-dot ${i === qi ? 'aj-dot--active' : i < qi ? 'aj-dot--done' : ''}`} />
        ))}
      </div>
      <div className="aj-question" data-testid="aj-question">
        <p className="aj-q-text">{question.q}</p>
        <div className="aj-q-opts">
          {question.opts.map((o, i) => (
            <button key={i} className="aj-q-opt" data-testid={`aj-opt-${i}`} onClick={() => handleAnswer(o.type)}>{o.text}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MazeLevel({ onComplete, onBack }) {
  const SIZE = 11;
  const maze = useMemo(() => generateMaze(SIZE, SIZE), []);
  const [pos, setPos] = useState([0, 0]);
  const [won, setWon] = useState(false);
  const goal = [SIZE - 1, SIZE - 1];

  const move = useCallback((dr, dc) => {
    if (won) return;
    const nr = pos[0] + dr, nc = pos[1] + dc;
    if (nr >= 0 && nr < SIZE && nc >= 0 && nc < SIZE && maze[nr][nc] === 0) {
      setPos([nr, nc]);
      if (nr === goal[0] && nc === goal[1]) setWon(true);
    }
  }, [pos, won, maze, goal]);

  useEffect(() => {
    const handler = (e) => {
      const map = { ArrowUp: [-1,0], ArrowDown: [1,0], ArrowLeft: [0,-1], ArrowRight: [0,1] };
      if (map[e.key]) { e.preventDefault(); move(...map[e.key]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [move]);

  if (won) {
    return (
      <div className="aj-screen aj-result" data-testid="aj-maze-result">
        <div className="aj-result-orb">🗺️</div>
        <h2 className="aj-alchemist-type">Maze Complete!</h2>
        <p className="aj-section-sub">You found your way through the Maze of Life.</p>
        <div className="aj-guide" style={{ marginTop: '1.5rem' }}>
          <p className="aj-guide-name">Zuri speaks</p>
          <p className="aj-guide-text">"You navigated the Maze. Not around it — THROUGH it. That is the way of the Alchemist. Now you are ready to build your Vision."</p>
        </div>
        <button className="village-btn village-btn--primary aj-btn" data-testid="aj-maze-continue" onClick={onComplete}>Continue to Level 3</button>
      </div>
    );
  }

  return (
    <div className="aj-screen aj-maze" data-testid="aj-maze">
      <button className="aj-back" onClick={onBack}><ChevronLeft size={16} /> Level Map</button>
      <h2 className="aj-section-title">Level 2: The Maze</h2>
      <p className="aj-section-sub">Navigate the Maze of Life — find your path</p>
      <p className="aj-maze-hint">Zuri: "Move toward the golden star. Use arrow keys or the buttons below."</p>
      <div className="aj-maze-grid" data-testid="aj-maze-grid">
        {maze.map((row, r) => (
          <div key={r} className="aj-maze-row">
            {row.map((cell, c) => {
              const isPlayer = pos[0] === r && pos[1] === c;
              const isGoal = r === goal[0] && c === goal[1];
              return (
                <div key={c} className={`aj-maze-cell ${cell === 1 ? 'aj-wall' : ''} ${isPlayer ? 'aj-player' : ''} ${isGoal ? 'aj-goal' : ''}`}>
                  {isPlayer && '🌿'}
                  {isGoal && !isPlayer && '⭐'}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="aj-maze-controls" data-testid="aj-maze-controls">
        <div /><button className="aj-maze-btn" onClick={() => move(-1, 0)}><ArrowUp size={20} /></button><div />
        <button className="aj-maze-btn" onClick={() => move(0, -1)}><ArrowLeft size={20} /></button>
        <div />
        <button className="aj-maze-btn" onClick={() => move(0, 1)}><ArrowRight size={20} /></button>
        <div /><button className="aj-maze-btn" onClick={() => move(1, 0)}><ArrowDown size={20} /></button><div />
      </div>
    </div>
  );
}

function VisionLevel({ onComplete, onBack }) {
  const [selected, setSelected] = useState([]);

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 5 ? [...prev, id] : prev);
  };

  return (
    <div className="aj-screen aj-vision" data-testid="aj-vision">
      <button className="aj-back" onClick={onBack}><ChevronLeft size={16} /> Level Map</button>
      <h2 className="aj-section-title">Level 3: The Vision</h2>
      <p className="aj-section-sub">What do you want? Build your Alchemy Vision</p>
      <div className="aj-guide" style={{ marginBottom: '1.5rem' }}>
        <p className="aj-guide-name">Zuri speaks</p>
        <p className="aj-guide-text">"Now that you know WHO you are, it's time to ask — WHAT do you want? And WHY do you want it? Choose up to 5 visions that call to your soul."</p>
      </div>
      <div className="aj-vision-grid" data-testid="aj-vision-grid">
        {VISION_ITEMS.map(v => (
          <button key={v.id} className={`aj-vision-card ${selected.includes(v.id) ? 'aj-vision--selected' : ''}`} data-testid={`aj-vision-${v.id}`} onClick={() => toggle(v.id)}>
            <span className="aj-vision-icon">{v.icon}</span>
            <span className="aj-vision-label">{v.label}</span>
          </button>
        ))}
      </div>
      <button
        className="village-btn village-btn--primary aj-btn"
        data-testid="aj-vision-seal"
        disabled={selected.length === 0}
        style={{ opacity: selected.length > 0 ? 1 : 0.4 }}
        onClick={() => onComplete(selected.map(id => VISION_ITEMS.find(v => v.id === id).label))}
      >
        Seal My Vision ({selected.length}/5)
      </button>
    </div>
  );
}

function HarvestLevel({ alchemistType, visions, onComplete, onBack }) {
  return (
    <div className="aj-screen aj-harvest" data-testid="aj-harvest">
      <button className="aj-back" onClick={onBack}><ChevronLeft size={16} /> Level Map</button>
      <h2 className="aj-section-title">Level 4: The Harvest</h2>
      <p className="aj-section-sub">Your transformation is revealed</p>
      <div className="aj-harvest-card" data-testid="aj-harvest-card">
        <div className="aj-harvest-type">
          <span className="aj-harvest-orb">🔮</span>
          <h3>The {alchemistType}</h3>
        </div>
        <div className="aj-harvest-visions">
          <p className="aj-harvest-label">Your Vision</p>
          <div className="aj-harvest-tags">
            {visions.map((v, i) => <span key={i} className="aj-harvest-tag">{v}</span>)}
          </div>
        </div>
        <div className="aj-harvest-declaration">
          <p>"I am The {alchemistType}. I choose {visions[0] || 'my path'}. I choose {visions[1] || 'my truth'}. I choose ME. F.U.C.K. T.H.A.T.T."</p>
        </div>
      </div>
      <div className="aj-guide" style={{ marginTop: '1.5rem' }}>
        <p className="aj-guide-name">Zuri speaks</p>
        <p className="aj-guide-text">"You have walked the path. You have answered the three questions. You have become the Alchemist. Now enter the final chamber..."</p>
      </div>
      <button className="village-btn village-btn--primary aj-btn" data-testid="aj-harvest-continue" onClick={onComplete}>Enter: God in ME</button>
    </div>
  );
}

function GodInMe({ alchemistType, visions }) {
  const [showShare, setShowShare] = useState(false);

  return (
    <div className="aj-screen aj-godInMe" data-testid="aj-god-in-me">
      <h1 className="aj-god-title">God in ME</h1>
      <p className="aj-god-sub">The Legend Level — You Have Arrived</p>
      <div className="aj-god-orb">🔥</div>
      <h2 className="aj-god-legend">You Are Legend</h2>
      <p className="aj-section-sub">The God in ME sees the God in YOU</p>
      <div className="aj-god-vip" data-testid="aj-vip-unlock">
        <span className="aj-god-crown">👑</span>
        <h3>VIP Access Unlocked</h3>
        <p>You've earned monthly group coaching + direct access to Cabrini. This is the inner circle — the space where Alchemists gather and grow together. Be. Do. Have.</p>
      </div>
      <blockquote className="aj-god-declaration" data-testid="aj-declaration">
        "I am not who I was. I have walked the Maze, built my Vision, and claimed my Harvest. The God in ME is awake. I Choose ME. F.U.C.K. T.H.A.T.T."
      </blockquote>
      <div className="aj-result-btns">
        <button className="village-btn village-btn--secondary aj-btn" data-testid="aj-god-share" onClick={() => setShowShare(true)}>
          <Share2 size={16} /> Share My Journey
        </button>
        <Link to="/life-map" className="village-btn village-btn--primary aj-btn" data-testid="aj-return-village">Return to the Village</Link>
      </div>
      {showShare && <ShareCard alchemistType={alchemistType || 'Alchemist'} visions={visions} onClose={() => setShowShare(false)} />}
    </div>
  );
}

/* ─── MAIN ─── */
export default function AlchemyJourney() {
  const [screen, setScreen] = useState('welcome');
  const [xp, setXp] = useState(0);
  const [unlocked, setUnlocked] = useState(1);
  const [alchemistType, setAlchemistType] = useState('');
  const [visions, setVisions] = useState([]);

  const addXp = (amount) => setXp(prev => prev + amount);

  return (
    <section className="aj" data-testid="alchemy-journey">
      <div className="aj-stars" />
      <XPBar xp={xp} />
      <div className="aj-container">
        {screen === 'welcome' && <WelcomeScreen onBegin={() => setScreen('map')} onMap={() => setScreen('map')} />}
        {screen === 'map' && <LevelMap unlocked={unlocked} onSelect={(n) => setScreen(`level${n}`)} onBack={() => setScreen('welcome')} />}
        {screen === 'level1' && (
          <QuizLevel
            onComplete={(type) => { setAlchemistType(type); addXp(100); setUnlocked(u => Math.max(u, 2)); setScreen('map'); }}
            onBack={() => setScreen('map')}
          />
        )}
        {screen === 'level2' && (
          <MazeLevel
            onComplete={() => { addXp(150); setUnlocked(u => Math.max(u, 3)); setScreen('map'); }}
            onBack={() => setScreen('map')}
          />
        )}
        {screen === 'level3' && (
          <VisionLevel
            onComplete={(v) => { setVisions(v); addXp(100); setUnlocked(u => Math.max(u, 4)); setScreen('map'); }}
            onBack={() => setScreen('map')}
          />
        )}
        {screen === 'level4' && (
          <HarvestLevel
            alchemistType={alchemistType || 'Seeker'}
            visions={visions.length ? visions : ['Purpose', 'Peace']}
            onComplete={() => { addXp(150); setScreen('godInMe'); }}
            onBack={() => setScreen('map')}
          />
        )}
        {screen === 'godInMe' && <GodInMe alchemistType={alchemistType} visions={visions} />}
      </div>
    </section>
  );
}
